import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useGSAP } from '@/composables/useGSAP'

export type EyeProps = React.HTMLProps<HTMLDivElement> & {
  childrenBackground?: React.ReactNode
  childrenBetween?: React.ReactNode
  appear?: boolean
  appearFull?: boolean
  delay?: number
  defaultOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  isSmallMobile?: boolean
  isLargeMobile?: boolean
  hasWaitingBlink?: boolean
  clipOnMobile?: boolean
}

export type EyeRef = {
  rootRef: React.MutableRefObject<HTMLDivElement | null>
  close: ({ blink, fromFull }: { blink?: boolean; fromFull?: boolean }) => void
  open: ({ delay, toFull }: { delay?: number; toFull?: boolean }) => void
  blink: () => void
}

const Eye = forwardRef<EyeRef, EyeProps>(
  (
    {
      className,
      children,
      childrenBackground,
      childrenBetween,
      appear = false,
      appearFull = false,
      delay = 0,
      defaultOpen = true,
      onOpen,
      onClose,
      isLargeMobile = false,
      isSmallMobile = false,
      hasWaitingBlink = false,
      clipOnMobile = true,
    },
    ref
  ) => {
    const id = useId()
    const { gsap } = useGSAP()
    const rootRef = useRef<HTMLDivElement>(null)
    const svgRef = useRef<SVGPathElement>(null)
    const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth)
    const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight)
    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen)

    const [from, setFrom] = useState<string>('')
    const [to, setTo] = useState<string>('')
    // const [offsetX, setOffsetX] = useState<number>(0);

    const [isDesktop, setIsDesktop] = useState(false)

    const [state, setState] = useState<string>(defaultOpen ? (appearFull ? 'open_full' : 'open') : 'closed')
    const [needResize, setNeedResize] = useState<boolean>(false)

    const close = useCallback(
      ({ blink = true, fromFull = false }) => {
        const ctx = gsap.context(() => {
          // console.log('[Eye] close', { blink, fromFull })

          const timeline = gsap.timeline({
            onComplete: () => {
              setIsOpen(false)
              setState('closed')
            },
          })

          timeline.fromTo(
            svgRef.current,
            {
              morphSVG: from,
            },
            {
              morphSVG: to,
              duration: 0.2,
              ease: 'power4.in',
            },
            0
          )
          if (blink) {
            timeline.to(svgRef.current, {
              morphSVG: from,
              duration: 0.15,
              ease: 'power4.out',
            })
            timeline.to(svgRef.current, {
              morphSVG: to,
              duration: 0.4,
              ease: 'power4.in',
            })
          }

          if (fromFull) {
            timeline.fromTo(
              svgRef.current,
              {
                scale: 2.05,
                transformOrigin: 'center',
              },
              {
                scale: 1,
                duration: 0.2,
                ease: 'power1.out',
              },
              0
            )
          }

          timeline.add(onClose, 0)
        })

        return () => ctx.kill()
      },
      [gsap, from, to, onClose]
    )

    const open = useCallback(
      ({ delay = 0, toFull = false }) => {
        const ctx = gsap.context(() => {
          // console.log('[Eye] open', { delay, toFull })
          const timeline = gsap.timeline({
            delay,
            onStart: () => {
              setIsOpen(true)
            },
            onComplete: () => {
              setState(toFull ? 'open_full' : 'open')
            },
          })

          timeline.fromTo(
            svgRef.current,
            {
              morphSVG: to,
            },
            {
              morphSVG: from,
              duration: 0.4,
              ease: 'power4.out',
            },
            0
          )
          if (toFull) {
            // console.log('[Eye] open toFull scale', { svg: svgRef.current })
            timeline.fromTo(
              svgRef.current,
              {
                scale: 1,
                transformOrigin: 'center',
              },
              {
                scale: 2.05,
                // scale: 10,
                duration: 0.4,
                ease: 'power4.inOut',
              },
              0
            )
          }

          timeline.add(onOpen, 0)
        })
        return () => ctx.kill()
      },
      [gsap, from, to, onOpen]
    )

    const blink = useCallback(() => {
      const ctx = gsap.context(() => {
        gsap
          .timeline()
          .to(svgRef.current, {
            morphSVG: from,
            duration: 0.15,
            ease: 'power4.out',
          })
          .to(svgRef.current, {
            morphSVG: to,
            duration: 0.4,
            ease: 'power4.in',
          })
          .to(svgRef.current, {
            morphSVG: from,
            duration: 0.15,
            ease: 'power4.out',
          })
      })

      return () => ctx.kill()
    }, [gsap, from, to])

    useImperativeHandle(ref, () => {
      return {
        rootRef,
        close,
        open,
        blink,
      }
    })

    useLayoutEffect(() => {
      let isFirst = true
      const onResize = () => {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches
        const svgW = 1440
        const svgH = 844
        let vW = isDesktop ? rootRef.current?.offsetWidth : svgW
        let vH = isDesktop ? rootRef.current?.offsetHeight : svgH

        let oX = 0
        let oY = 0

        if (isSmallMobile) {
          vW = rootRef.current?.offsetWidth
          vH = rootRef.current?.offsetHeight
        }
        if (!isDesktop && isLargeMobile) {
          vH = rootRef.current?.offsetHeight * 0.8
          vW = (vH * svgW) / svgH

          oY = rootRef.current?.offsetHeight * 0.1
          oX = (vW - rootRef.current?.offsetWidth) / 2
        }

        const from = `M${(1298.35 / svgW) * vW - oX} ${(417.348 / svgH) * vH + oY} 
                    C${(1300.59 / svgW) * vW - oX} ${(420.164 / svgH) * vH + oY} ${(1300.59 / svgW) * vW - oX} ${(423.836 / svgH) * vH + oY} ${(1298.35 / svgW) * vW - oX} ${(426.652 / svgH) * vH + oY} 
                    C${(1265.78 / svgW) * vW - oX} ${(467.529 / svgH) * vH + oY} ${(1019.27 / svgW) * vW - oX} ${(764 / svgH) * vH + oY} ${(720 / svgW) * vW - oX} ${(764 / svgH) * vH + oY} 
                    C${(420.729 / svgW) * vW - oX} ${(764 / svgH) * vH + oY} ${(174.216 / svgW) * vW - oX} ${(467.529 / svgH) * vH + oY} ${(141.65 / svgW) * vW - oX} ${(426.652 / svgH) * vH + oY} 
                    C${(139.406 / svgW) * vW - oX} ${(423.836 / svgH) * vH + oY} ${(139.406 / svgW) * vW - oX} ${(420.164 / svgH) * vH + oY} ${(141.65 / svgW) * vW - oX} ${(417.348 / svgH) * vH + oY} 
                    C${(174.216 / svgW) * vW - oX} ${(376.471 / svgH) * vH + oY} ${(420.729 / svgW) * vW - oX} ${(80 / svgH) * vH + oY} ${(720 / svgW) * vW - oX} ${(80 / svgH) * vH + oY} 
                    C${(1019.27 / svgW) * vW - oX} ${(80 / svgH) * vH + oY} ${(1265.78 / svgW) * vW - oX} ${(376.471 / svgH) * vH + oY} ${(1298.35 / svgW) * vW - oX} ${(417.348 / svgH) * vH + oY}Z`
        const to = `M${(1275.95 / svgW) * vW - oX} ${(421.908 / svgH) * vH + oY}
                  C${(1276.08 / svgW) * vW - oX} ${(421.909 / svgH) * vH + oY} ${(1276.08 / svgW) * vW - oX} ${(422.091 / svgH) * vH + oY} ${(1275.95 / svgW) * vW - oX} ${(422.092 / svgH) * vH + oY}
                  C${(1202.38 / svgW) * vW - oX} ${(422.339 / svgH) * vH + oY} ${(981.301 / svgW) * vW - oX} ${(423 / svgH) * vH + oY} ${(720 / svgW) * vW - oX} ${(423 / svgH) * vH + oY}
                  C${(458.699 / svgW) * vW - oX} ${(423 / svgH) * vH + oY} ${(237.618 / svgW) * vW - oX} ${(422.339 / svgH) * vH + oY} ${(164.048 / svgW) * vW - oX} ${(422.092 / svgH) * vH + oY}
                  C${(163.915 / svgW) * vW - oX} ${(422.091 / svgH) * vH + oY} ${(163.915 / svgW) * vW - oX} ${(421.909 / svgH) * vH + oY} ${(164.048 / svgW) * vW - oX} ${(421.908 / svgH) * vH + oY}
                  C${(237.618 / svgW) * vW - oX} ${(421.661 / svgH) * vH + oY} ${(458.699 / svgW) * vW - oX} ${(421 / svgH) * vH + oY} ${(720 / svgW) * vW - oX} ${(421 / svgH) * vH + oY}
                  C${(981.301 / svgW) * vW - oX} ${(421 / svgH) * vH + oY} ${(1202.38 / svgW) * vW - oX} ${(421.661 / svgH) * vH + oY} ${(1275.95 / svgW) * vW - oX} ${(421.908 / svgH) * vH + oY}Z`

        setViewportWidth(vW)
        setViewportHeight(vH)
        // setOffsetX(oX)
        setIsDesktop(isDesktop)

        setFrom(from)
        setTo(to)

        if (isFirst) {
          isFirst = false
          svgRef.current?.setAttribute('d', from)
          waitingPathRef.current?.setAttribute('d', from)
        } else {
          setNeedResize(true)
        }
        // console.log('[Eye] onResize setSVG', { from })
      }

      onResize()
      window.addEventListener('resize', onResize)

      return () => {
        window.removeEventListener('resize', onResize)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if ((appear || appearFull) && defaultOpen && from !== '' && to !== '') {
        open({ delay, toFull: appearFull })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to])

    useLayoutEffect(() => {
      const ctx = gsap.context(() => {
        if (needResize) {
          // console.log('[Eye] onResize setState', { state })

          setTimeout(() => {
            switch (state) {
              case 'closed':
                gsap
                  .timeline()
                  .set(svgRef.current, { x: 0, y: 0, clearProps: 'all' })
                  .set(
                    svgRef.current,
                    { morphSVG: to, scale: 1, transformOrigin: 'center', immediateRender: false },
                    0.1
                  )
                break
              case 'open':
                gsap
                  .timeline()
                  .set(svgRef.current, { x: 0, y: 0, clearProps: 'all' })
                  .set(
                    svgRef.current,
                    { morphSVG: from, scale: 1, transformOrigin: 'center', immediateRender: false },
                    0.1
                  )
                break
              case 'open_full':
                gsap
                  .timeline()
                  .set(svgRef.current, { clearProps: 'all' })
                  .set(
                    svgRef.current,
                    { morphSVG: from, scale: 2.05, transformOrigin: 'center', immediateRender: false },
                    0.1
                  )
                break
            }
          }, 0)

          setNeedResize(false)
        }
      })

      return () => ctx.kill()
    }, [gsap, state, from, to, needResize])

    const waitingPathRef = useRef<SVGPathElement>(null)

    useLayoutEffect(() => {
      const ctx = gsap.context(() => {
        if (!hasWaitingBlink || from === '' || to === '') return
        gsap
          .timeline({ repeat: -1, repeatDelay: 2, yoyo: true, delay: 1 })
          .fromTo(
            waitingPathRef.current,
            {
              morphSVG: from,
            },
            {
              morphSVG: to,
              duration: 0.4,
              ease: 'blinkEye2',
            },
            0
          )
          .fromTo(
            waitingPathRef.current,
            {
              morphSVG: from,
            },
            {
              morphSVG: to,
              duration: 0.25,
              ease: 'blinkEye1',
            },
            '+=1.7'
          )
          .fromTo(
            waitingPathRef.current,
            {
              morphSVG: from,
            },
            {
              morphSVG: to,
              duration: 0.3,
              ease: 'blinkEye2',
            },
            '+=0.0'
          )
          .fromTo(
            waitingPathRef.current,
            {
              morphSVG: from,
            },
            {
              morphSVG: to,
              duration: 0.5,
              ease: 'blinkEye2',
            },
            '+=2.2'
          )
          .fromTo(
            waitingPathRef.current,
            {
              morphSVG: from,
            },
            {
              morphSVG: to,
              duration: 0.3,
              ease: 'blinkEye1',
            },
            '+=1.6'
          )
      })

      return () => ctx.revert()
    }, [hasWaitingBlink, gsap, from, to, isOpen])

    return (
      <div ref={rootRef} className={className}>
        <svg
          aria-hidden='true'
          className='absolute top-0 left-0 w-full h-full block'
          viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          preserveAspectRatio='none'
        >
          <defs>
            <clipPath id={`clipPath-${id}`} clipPathUnits='userSpaceOnUse'>
              <path ref={svgRef} d='' fill='black' />
            </clipPath>

            <clipPath id={`waiting-${id}`} clipPathUnits='userSpaceOnUse'>
              <path ref={waitingPathRef} d='' fill='black' />
            </clipPath>
          </defs>
        </svg>

        {childrenBackground && (
          <div
            className={`Eye_Background absolute top-0 left-0 w-full h-full ${isOpen ? '' : 'opacity-0'}`}
            style={{
              clipPath: `url(#clipPath-${id})`,
              // "-webkit-clipPath": `url(#clipPath-${id})`,
              transform: 'translateZ(0)',
            }}
          >
            {hasWaitingBlink ? (
              <div
                className={`Eye_Background_Inner absolute top-0 left-0 w-full h-full`}
                style={{
                  clipPath: `url(#waiting-${id})`,
                  // "-webkit-clipPath": `url(#waiting-${id})`,
                  transform: 'translateZ(0)',
                }}
              >
                {childrenBackground}
              </div>
            ) : (
              childrenBackground
            )}
          </div>
        )}
        {childrenBetween}
        <div
          className={`Eye_Inner relative w-full h-full ${isOpen ? '' : 'opacity-0'}`}
          style={
            isDesktop || (!isDesktop && isSmallMobile) || clipOnMobile
              ? {
                  clipPath: `url(#clipPath-${id})`,
                  // "-webkit-clipPath": `url(#clipPath-${id})`,
                  transform: 'translateZ(0)',
                }
              : {}
          }
        >
          {hasWaitingBlink ? (
            <div
              className={`Eye_Subinner relative w-full h-full`}
              style={
                isDesktop || (!isDesktop && isSmallMobile) || clipOnMobile
                  ? {
                      clipPath: `url(#waiting-${id})`,
                      // "-webkit-clipPath": `url(#waiting-${id})`,
                      transform: 'translateZ(0)',
                    }
                  : {}
              }
            >
              {children}
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    )
  }
)
export default Eye
